import type {JSX} from 'react';
import {useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';

import {hasAdminPermission} from '@afriex/core/utils';

import type {IMenuConfig, ISubMenuConfig} from '@/config/menuConfig';
import {menuConfig as menuItems} from '@/config/menuConfig';
import {useTransactionContext} from '@/context/TransactionContext';
import {useAuth} from '@/hooks/useAuth';
import {useCountryListFetcher} from '@/hooks/useCountryListFetcher';
import {useActiveFeatures} from '@/hooks/useFeatureActive';
import {useRatesFetcher} from '@/hooks/useRatesFetcher';

import {findActiveSubMenuIndex} from './findActiveSubMenuIndex';

const navClasses = 'p-2 pl-4 w-4/4 mt-1';
const activeNavClasses =
  'p-2 pl-4 bg-blue-50 dark:bg-dark-blueBg rounded-md mt-1';
const exactNavClasses = 'text-cyan-600 font-semibold';
const getNavTestId = (...label: string[]) => {
  const testId = label
    .filter(Boolean)
    .map((part) =>
      part
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    )
    .join('-');

  return label.length === 1 ? `admin-nav-${testId}-link` : testId;
};

type NavProps = {
  closeMenu: () => void;
};

export default function Nav({closeMenu}: NavProps): JSX.Element {
  const location = useLocation();
  const {role} = useAuth();
  const {menuSuffixes} = useTransactionContext();
  useRatesFetcher();
  useCountryListFetcher();

  return (
    <nav className="px-3 flex flex-col flex-1 overflow-y-auto dark:text-white">
      <ul className="flex flex-col">
        {menuItems.map((menuItem) => {
          if (
            menuItem.permission &&
            !hasAdminPermission(role, menuItem.permission)
          ) {
            return null;
          }
          const path = `/${menuItem.path}`;
          const Icon = menuItem.icon;
          const isActive =
            location.pathname === path ||
            location.pathname.startsWith(`${path}/`);
          return (
            <li key={menuItem.label}>
              <Link
                to={path}
                onClick={closeMenu}
                data-cy={getNavTestId(menuItem.label)}
                className={`flex items-center gap-1 dark:hover:text-[#0075FF] ${
                  isActive ? activeNavClasses : navClasses
                }`}
              >
                {Icon && (
                  <Icon
                    className={`w-5 h-5 ${
                      path === location.pathname && exactNavClasses
                    }`}
                  />
                )}

                <span
                  className={`${path === location.pathname && exactNavClasses}`}
                >
                  {menuItem.label}
                </span>
              </Link>

              {isActive && (
                <SubMenus
                  menuItem={menuItem}
                  role={role}
                  closeMenu={closeMenu}
                  currentLocation={location.pathname}
                  suffixes={menuSuffixes}
                />
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

type SubMenuProps = {
  menuItem: IMenuConfig;
  role?: string;
  closeMenu: () => void;
  currentLocation: string;
  suffixes: Record<string, string>;
};

function SubMenus({
  menuItem,
  role,
  closeMenu,
  currentLocation,
  suffixes,
}: SubMenuProps): JSX.Element {
  const location = useLocation();
  const [currentStatusRoute, setCurrentStatusRoute] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setCurrentStatusRoute(currentLocation + location.search);
  }, [currentLocation, location]);

  // An entry tied to a feature shows only once that feature is on for anyone.
  const {active: activeFeatures} = useActiveFeatures(
    menuItem.subMenu.flatMap((item) =>
      item.featureCode ? [item.featureCode] : [],
    ),
  );

  // A section can hold entries that need a permission its own page does not.
  const visibleItems = menuItem.subMenu.filter(
    (item) =>
      (!item.featureCode || activeFeatures.has(item.featureCode)) &&
      (!item.permission || hasAdminPermission(role, item.permission)),
  );

  // At most one item per section is active: the most specific match.
  const activeIndex = currentStatusRoute
    ? findActiveSubMenuIndex(
        currentStatusRoute,
        visibleItems.map((item) => item.path),
      )
    : -1;

  const subMenus = visibleItems.map((item: ISubMenuConfig, index) => {
    const subMenuPath = `/${item.path}`;
    const Icon = item.icon;
    const isActive = index === activeIndex;
    return (
      <li
        key={index}
        onClick={() => setCurrentStatusRoute(subMenuPath)}
        className={`flex items-center p-2 pl-4 mt-1 ${
          isActive && exactNavClasses
        } m-0 p-0 ml-5 pl-3 gap-1`}
      >
        {Icon && <Icon className="w-5 h-5" />}
        <Link
          to={item.path}
          onClick={closeMenu}
          data-cy={getNavTestId(menuItem.label, item.label)}
          className="text-sm"
        >
          {item.label} {suffixes[item.path] && suffixes[item.path]}
        </Link>
      </li>
    );
  });

  return <ul className="mt-0">{subMenus}</ul>;
}
